import React, { createRef, PureComponent } from 'react';
import { array, bool, func } from 'prop-types';
import { BubbleInstance } from './BubbleInstance.js';
import style from './displayEmoji.module.css';
/**
 * @typedef {import('lottie-web')} LottiePlayer
 */
/** @type {LottiePlayer} */
import LottiePlayer from 'lottie-web';
// import FPS from '../../../../../lib/FPS.js';
// import quantify from '../../../../../lib/quantify.js';
import { BubblingPool } from './BubblingPool.jsx';
import scopeLogger from 'lib/scope-logger';

const console = scopeLogger('RMI:emoji:DisplayEmoji');

export default class DisplayEmoji extends PureComponent {
  static propTypes = {
    emojiArray: array,
    displayQueue: array,
    clearDisplayQueue: func.isRequired,
    enableFPSWatch: bool,
    silentMode: bool,
    isHidden: bool,
  };
  static defaultProps = {
    emojiArray: [],
    enableFPSWatch: false,
  };
  levelFPS;
  /**
   *
   * @type {React.RefObject<Element>}
   */
  ref = createRef();
  pendingPollInstances = 0;
  teardown = [];
  /**
   * @typedef {Object} poolItem
   * @property {string} name
   * @property {BubbleInstance} bubbleInstance
   * @property {Element} container
   * @property {string} state
   */

  /**
   *
   * @type {poolItem[]}
   */
  instancesPool = [];
  paintDone = false;

  constructor(props) {
    super(props);
    /**
     * Bubbling pool size
     * bubbling up duration 5s
     */
    this.pollSize = Math.round(3 * props.performanceRate);
    // Instead attempt to manipulate quality in real time set it for all instance
    if (props.performanceRate <= 3) {
      LottiePlayer.setQuality('middle');
    }
  }

  onPaintDone = (event) => {
    if (event.animationName === style.trigger) {
      // it will also catch bubbled events from every emoji, but who cares
      this.paintDone = true;
      this.applyNewSize();
    }
  };

  /*
  onFPS(fps) {
    const currentLevel = quantify(fps, [80, 65, 45, 25, 0]).findIndex(Boolean);

    // console.log(currentLevel, this.levelFPS);

    if (currentLevel === this.levelFPS) {
      return;
    }

    /!*
        if (currentLevel <= 1 && this.levelFPS > 1) {
          LottiePlayer.setQuality('high');
          console.log('Restore animation quality to high.');
        }
    *!/

    if (currentLevel <= 2 && this.levelFPS > 2) {
      LottiePlayer.unfreeze();
      // console.log('Well enough FPS. Unfreeze animation.');
    }

    /!*
        if (currentLevel >= 3 && this.levelFPS < 3) {
          LottiePlayer.setQuality('middle');
          console.warn('Low FPS %s. Dropdown animation quality to middle.', fps);
        }
    *!/

    if (currentLevel >= 4 && this.levelFPS < 4) {
      LottiePlayer.freeze();
      console.warn('Too low FPS: %s. Freeze animation', fps);
      //  TODO: Should we send some kind event in such case?
    }

    this.levelFPS = currentLevel;
  }
*/

  componentDidMount() {
    this.computedStyles = window.getComputedStyle(this.ref.current);
    // this.props.enableFPSWatch && this.teardown.push(FPS(this.onFPS.bind(this)))
  }

  componentWillUnmount() {
    this.teardown.forEach((fn) => {
      // console.info(`Tear down: ${fn.name}`);
      fn();
    });
  }

  componentDidUpdate(prevProps, _prevState, _snapshot) {
    const isSilentModeChanged = prevProps.silentMode !== this.props.silentMode;
    const isHiddenChanged = prevProps.isHidden !== this.props.isHidden;

    if (this.props.silentMode && this.props.isHidden) {
      return;
    }

    this.computedStyles = window.getComputedStyle(this.ref.current);

    if (isSilentModeChanged || isHiddenChanged) {
      this.onChangeMode();
    }

    this.handleQueue(this.props.displayQueue, this.props.clearDisplayQueue);
  }

  getAnimation(animationName) {
    const icon = this.props.emojiArray.find(
      ({ name }) => name === animationName,
    );
    const animationData = this.props.getAssetByUrl(icon.url);
    return Object.assign({}, icon, { data: animationData?.data });
  }

  registerInstance = (name) => {
    // const canvasSize = this.getCanvasSize();
    const animation = this.getAnimation(name);

    return (container) => {
      /*
            if (bubbleInstance && !(name in this.instances)) {
              this.instances[name] = bubbleInstance;
            }
      */

      if (container === null) {
        return;
      }

      if (!animation.data) {
        console.warn(
          `Could not register instance for display "${name}" emoji, animation data not ready.`,
        );
        return;
      }

      const bubbleInstance = new BubbleInstance(
        LottiePlayer,
        container,
        animation.data,
        void 0,
      );
      this.pendingPollInstances++;
      this.instancesPool.push({
        name,
        container,
        bubbleInstance,
        state: 'pending',
      });
      this.teardown.push(() => bubbleInstance.destroy());
      /**
       * TODO: After switching from per instance loading lottie data it seems redundant
       *    moreover, it bring unnecessary complexity in instance constructor
       */
      bubbleInstance.onReady((event) => {
        this.pendingPollInstances--;
        this.consumable = this.pendingPollInstances === 0;
        this.instancesPool = this.instancesPool.map((item) => {
          return item.container === event.target
            ? {
                ...item,
                state: 'ready',
              }
            : item;
        });
      });

      bubbleInstance.onStart((event) => {
        // console.time('changeState to busy')
        this.instancesPool = this.instancesPool.map((item) => {
          return item.container === event.target
            ? {
                ...item,
                state: 'busy',
              }
            : item;
        });
        // console.timeEnd('changeState to busy')
      });

      bubbleInstance.onComplete((event) => {
        // console.time('changeState to ready')
        this.instancesPool = this.instancesPool.map((item) => {
          return item.container === event.target
            ? {
                ...item,
                state: 'ready',
              }
            : item;
        });
        // console.timeEnd('changeState to ready')
        this.forceUpdate();
      });
    };
  };

  getCanvasSize() {
    const heightProps = ['height', 'paddingTop', 'paddingBottom'];
    const widthProps = ['width', 'paddingRight', 'paddingLeft'];

    const asNumber = (propName) => parseInt(this.computedStyles[propName]);
    const [height, pT, pB] = heightProps.map(asNumber);
    const [width, pR, pL] = widthProps.map(asNumber);
    const canvasSize = {
      height: height - pT - pB,
      width: width - pR - pL,
    };

    // Sometimes it still may return NaN or calculate negative height
    // Not sure, should we handled it or just omit,
    // and prey that it's only debug mode peculiarity.
    if (Number.isNaN(canvasSize.height) || Number.isNaN(canvasSize.width)) {
      if (process.env.NODE_ENV === 'development') {
        console.debug([height, pT, pB], [width, pR, pL], canvasSize);
      }
      throw new Error("Seems browser's style applying still not fulfilled");
    }
    return canvasSize;
  }

  onChangeMode() {
    this.applyNewSize();
    this.instancesPool.forEach((instance) => {
      if (instance.state === 'busy') {
        instance.bubbleInstance.restart();
      }
    });
  }

  applyNewSize() {
    try {
      const canvasSize = this.getCanvasSize();
      this.instancesPool.forEach(
        ({ bubbleInstance }) => (bubbleInstance.canvasSize = canvasSize),
      );
      this.setBubblingHeight(canvasSize.height);
    } catch (error) {
      console.error('Something went wrong while try apply new size', error);
    }
  }

  setBubblingHeight(height) {
    this.ref.current.style.setProperty(
      '--bubbling-up-distance',
      `-${height}px`,
    );
  }

  handleQueue(displayQueue, cb) {
    if (!this.consumable) {
      return;
    }

    const handled = displayQueue.reduce((_handled, item) => {
      return this.displayEmoji(item, _handled.length > 1)
        ? _handled.concat(item)
        : _handled;
    }, []);

    if (handled.length > 0) {
      cb(handled);
    }
  }

  displayEmoji(emoji, isOneOfSwarm) {
    const {
      // name,
      count,
      isOwn,
    } = emoji;

    if (this.props.silentMode && this.props.isHidden) {
      // e.g. panel is closed and silent mode is disabled
      console.info('fake render', {
        silentMode: this.props.silentMode,
        isHidden: this.props.isHidden,
      });
      return true;
    }

    const instances = this.instancesPool.filter(
      ({ name }) => name === emoji.name,
    );
    if (instances.length === 0) {
      console.warn(
        `Does not found any instance for display emoji "${emoji.name}"`,
      );
      return true; // just for removing from queue
    }

    const instance = instances.find(({ state }) => state === 'ready');

    if (!instance) {
      // console.info(`Does not found ready for displaying emoji "${emoji.name}" instance`)
      return false; // keep in queue for next attempt
    }
    /*
        if (!instance) {
          console.warn(`Emoji "${name}" does not match to any available instance`);
          return;
        }

        if (!instance.consumable) {
          console.warn(`Instance for emoji "${name}" does not ready.`);
          return;
        }

        if (!(instance instanceof BubbleInstance)) {
          console.warn(`Instance for emoji "${name}" bubbling is not an instance of BubbleInstance.`);
          return;
        }
    */

    instance.bubbleInstance.start(this.count2scale(count), {
      isOwn,
      isOneOfSwarm,
    });

    return true;
  }

  count2scale(count) {
    if (count === 1) {
      return 1;
    }
    return Math.min(1.5, 1 + count / 25);
  }

  render() {
    const {
      props: { isHidden, silentMode, emojiArray },
    } = this;

    /*
    // TODO: revert conditional render while refactoring that component
    if (silentMode && isHidden) {
      console.log('DisplayEmoji: render nothing', {silentMode, isHidden});
      return null;
    }

    console.log('DisplayEmoji: render', {silentMode, isHidden});
*/

    return (
      <section
        ref={this.ref}
        className={style.container}
        onAnimationEnd={this.onPaintDone}
        data-silent-mode={silentMode}
      >
        {emojiArray.map(({ name }) => (
          <BubblingPool
            key={name}
            name={name}
            pollSize={this.pollSize}
            register={this.registerInstance}
          />
        ))}
      </section>
    );
  }
}
